import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EntitiesContext, UserContext } from "../../App";
import { allowed } from "../../constants/config";
import {
  useCreateInventory,
  useUpdateInventory,
} from "../../hooks/useInventory";
import useInventoryMeta from "../../hooks/useInventoryMeta";
import InventoryRecord from "../../records/inventory";

export default function InventoryAdd({ sale }) {
  const params = useParams();
  const user = useContext(UserContext);
  const entities = useContext(EntitiesContext);
  const inventoryMeta = useInventoryMeta();
  const navigate = useNavigate(); // Initialize the navigate function

  const [errors, setErrors] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  // Consignors must accept the 20% cut and the pickup window before any
  // item is saved. This is not part of the item, so it lives outside the
  // InventoryRecord that gets sent to the API.
  const [acknowledged, setAcknowledged] = useState(false);
  const [item, setItem] = useState(
    new InventoryRecord({
      account_id: user.user.id,
      clothing_type: inventoryMeta.lists.clothingTypes.find(
        (l) => l.label === "NA",
      ).id,
      size: inventoryMeta.lists.sizes.find((l) => l.label === "NA").id,
      kids_size: inventoryMeta.lists.kidsSizes.find((l) => l.label === "NA").id,
      boot_size: inventoryMeta.lists.bootSizes.find((l) => l.label === "NA").id,
      event_type: inventoryMeta.lists.eventType.find((l) => l.label === "NA")
        .id,
      ski_type_nordic: inventoryMeta.lists.skiTypeNordic.find(
        (l) => l.label === "NA",
      ).id,
      boot_type_nordic: inventoryMeta.lists.bootTypeNordic.find(
        (l) => l.label === "NA",
      ).id,
      discounted: false,
    }),
  );

  const mutateInventory = (
    params.id ? useUpdateInventory : useCreateInventory
  )();

  useEffect(() => {
    if (params.id) {
      setItem(entities.state.get("inventory").get(parseInt(params.id)));
    }
  }, [params]);

  const restricted = (value) => {
    const regex = new RegExp(/helmet/i);
    return regex.test(value);
  };

  const onChange = (e) => {
    e.preventDefault();
    setItem(item.set(e.target.name, e.target.value));
  };

  const save = () => {
    const errs = validateFields();
    if (Object.keys(errs).length > 0) return setErrors(errs);

    mutateInventory.mutate(item, {
      onSuccess: () => {
        navigate("/dashboard/inventory", { replace: true }); // Show the saved item in the inventory list
      },
    });
  };

  const saveAndAddAnother = () => {
    const errs = validateFields();
    if (Object.keys(errs).length > 0) return setErrors(errs);

    mutateInventory.mutate(item, {
      onSuccess: () => {
        setShowNotification(true); // Show notification on success
        setItem(
          new InventoryRecord({
            account_id: user.user.id,
            clothing_type: inventoryMeta.lists.clothingTypes.find(
              (l) => l.label === "NA",
            ).id,
            size: inventoryMeta.lists.sizes.find((l) => l.label === "NA").id,
            kids_size: inventoryMeta.lists.kidsSizes.find(
              (l) => l.label === "NA",
            ).id,
            boot_size: inventoryMeta.lists.bootSizes.find(
              (l) => l.label === "NA",
            ).id,
            event_type: inventoryMeta.lists.eventType.find(
              (l) => l.label === "NA",
            ).id,
            ski_type_nordic: inventoryMeta.lists.skiTypeNordic.find(
              (l) => l.label === "NA",
            ).id,
            boot_type_nordic: inventoryMeta.lists.bootTypeNordic.find(
              (l) => l.label === "NA",
            ).id,
            discounted: false,
          }),
        );
        setErrors([]);
        setTimeout(() => setShowNotification(false), 3000); // Hide after 3 seconds
      },
    });
  };

  const validateFields = () => {
    let errs = {};
    const isNumber = /^\d+$/;

    if (item.price === "" || item.price <= 0)
      errs["price"] = "Price is required";
    if (item.name === "") errs["name"] = "Name is required";
    if (restricted(item.name)) errs["name"] = "Helmets cannot be consigned";
    if (!item.condition) errs["condition"] = "Condition is required";
    if (!item.category) errs["category"] = "Category is required";
    if (!isNumber.test(item.price)) errs["price"] = "Must be an integer";
    if (item.price && item.price < 5)
      errs["price"] = "Minimum price is $5";

    if (inventoryMeta.requiresLength(item.category) && item.length <= 0)
      errs["len"] = "Length is required";

    if (!acknowledged)
      errs["acknowledged"] = "You must acknowledge the consignment terms";

    return errs;
  };

  if (!allowed.includes(user.user.email) && sale.consignment_closed) {
    return (
      <div className="notification has-text-centered has-text-weight-semibold">
        <div>
          <p className="is-size-3">
            Consignment for the 2026 Waitsfield PTA Ski & Skate Sale has closed.
          </p>
        </div>
        <div className="mt-3">
          Your labels will be available to pickup on Nov. 11th after 8AM at
          Waitsfield Elementary School.
        </div>
        <div className="mt-3">
          Any unsold items can be picked up by 4:15PM (pick up is 3:45 - 4:15)
          on Saturday 11/14
          <br />
          Any items that are not picked up will become the property of the PTA.
        </div>
        <div className="mt-3 is-size-4">
          Be sure to check back here during and after the sale to see which
          items have sold!
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Notification */}
      {showNotification && (
        <div className="notification is-success">
          <button
            className="delete"
            onClick={() => setShowNotification(false)}
          ></button>
          Item has been successfully added!
        </div>
      )}

      {/* Form fields */}

      <div className="">
        <div className="field">
          <label className="label">
            Item Name <span style={{ color: "red" }}>*</span>
          </label>
          <div className="control">
            <input
              className="input"
              type="text"
              name="name"
              onChange={onChange}
              value={item.name}
            />
            <small className="has-text-grey">
              Include make, model, size and color eg: "Patagonia Nanopuff Black
              Women's Size Large"
            </small>
            {errors.name && (
              <div className="has-text-danger has-text-weight-semibold">
                {errors.name}
              </div>
            )}
          </div>
        </div>

        <div className="field">
          <label className="label">
            Price <span style={{ color: "red" }}>*</span>
          </label>
          <div className="field-body">
            <div className="field is-expanded">
              <div className="field has-addons">
                <p className="control">
                  <a className="button is-static">$</a>
                </p>
                <p className="control is-expanded">
                  <input
                    className="input"
                    name="price"
                    type="number"
                    onChange={onChange}
                    value={item.price}
                  />
                </p>
              </div>
              <small className="has-text-grey">
                Minimum price is $5.
              </small>
              {errors.price && (
                <div className="has-text-danger has-text-weight-semibold">
                  {errors.price}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="field">
          <label className="label">
            Condition <span style={{ color: "red" }}>*</span>
          </label>
          <div className="field-body">
            <div className="field is-narrow">
              <div className="control">
                <div className="select is-fullwidth">
                  <select
                    name="condition"
                    value={item.condition || ""}
                    onChange={onChange}
                  >
                    <option value="--">--</option>
                    {inventoryMeta.lists.conditions.valueSeq().map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {errors.condition && (
                <div className="has-text-danger has-text-weight-semibold">
                  {errors.condition}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="field">
          <label className="label">
            Category <span style={{ color: "red" }}>*</span>
          </label>
          <div className="field-body">
            <div className="field is-narrow">
              <div className="control">
                <div className="select is-fullwidth">
                  <select
                    name="category"
                    value={item.category || ""}
                    onChange={onChange}
                  >
                    <option value="--">--</option>
                    {inventoryMeta.lists.categories
                      .sortBy((c) => c.label)
                      .valueSeq()
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              {errors.category && (
                <div className="has-text-danger has-text-weight-semibold">
                  {errors.category}
                </div>
              )}
            </div>
          </div>
        </div>

        {inventoryMeta.category(item.category, "nordic boot") && (
          <div className="field">
            <label className="label">Nordic Boot Type</label>
            <div className="field-body">
              <div className="field is-narrow">
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      name="boot_type_nordic"
                      value={item.boot_type_nordic || ""}
                      onChange={onChange}
                    >
                      <option
                        value={
                          inventoryMeta.lists.skiTypeNordic.find(
                            (l) => l.label === "NA",
                          ).id
                        }
                      >
                        --
                      </option>
                      {inventoryMeta.lists.bootTypeNordic
                        .valueSeq()
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {inventoryMeta.category(item.category, "nordic ski") && (
          <div className="field">
            <label className="label">Nordic Ski Type</label>
            <div className="field-body">
              <div className="field is-narrow">
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      name="ski_type_nordic"
                      value={item.ski_type_nordic || ""}
                      onChange={onChange}
                    >
                      <option
                        value={
                          inventoryMeta.lists.skiTypeNordic.find(
                            (l) => l.label === "NA",
                          ).id
                        }
                      >
                        --
                      </option>
                      {inventoryMeta.lists.skiTypeNordic.valueSeq().map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {inventoryMeta.category(item.category, "race") && (
          <div className="field">
            <label className="label">Event Type</label>
            <div className="field-body">
              <div className="field is-narrow">
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      name="event_type"
                      value={item.event_type || ""}
                      onChange={onChange}
                    >
                      <option
                        value={
                          inventoryMeta.lists.eventType.find(
                            (l) => l.label === "NA",
                          ).id
                        }
                      >
                        --
                      </option>
                      {inventoryMeta.lists.eventType.valueSeq().map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {inventoryMeta.category(item.category, "accessories") && (
          <div className="field">
            <label className="label">Accessories</label>
            <div className="field-body">
              <div className="field is-narrow">
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      name="accessories"
                      value={item.accessories || ""}
                      onChange={onChange}
                    >
                      <option
                        value={
                          inventoryMeta.lists.accessories.find(
                            (l) => l.label === "NA",
                          )?.id || ""
                        }
                      >
                        --
                      </option>
                      {inventoryMeta.lists.accessories.valueSeq().map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {inventoryMeta.category(item.category, "clothing") && (
          <div className="field">
            <label className="label">Clothing Type</label>
            <div className="field-body">
              <div className="field is-narrow">
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      name="clothing_type"
                      value={item.clothing_type || ""}
                      onChange={onChange}
                    >
                      <option
                        value={
                          inventoryMeta.lists.clothingTypes.find(
                            (l) => l.label === "NA",
                          ).id
                        }
                      >
                        --
                      </option>
                      {inventoryMeta.lists.clothingTypes.valueSeq().map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {inventoryMeta.category(item.category, "clothing") &&
          !inventoryMeta.category(item.category, "kids") && (
            <div className="field">
              <label className="label">Size</label>
              <div className="field-body">
                <div className="field is-narrow">
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        name="size"
                        value={item.size || ""}
                        onChange={onChange}
                      >
                        <option
                          value={
                            inventoryMeta.lists.sizes.find(
                              (l) => l.label === "NA",
                            ).id
                          }
                        >
                          --
                        </option>
                        {inventoryMeta.lists.sizes.valueSeq().map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {inventoryMeta.category(item.category, "kids clothing") && (
          <div className="field">
            <label className="label">Kids Size</label>
            <div className="field-body">
              <div className="field is-narrow">
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      name="kids_size"
                      value={item.kids_size || ""}
                      onChange={onChange}
                    >
                      <option
                        value={
                          inventoryMeta.lists.kidsSizes.find(
                            (l) => l.label === "NA",
                          ).id
                        }
                      >
                        --
                      </option>
                      {inventoryMeta.lists.kidsSizes.valueSeq().map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {inventoryMeta.category(item.category, "boot") && (
          <div className="field">
            <label className="label">Boot Size</label>
            <div className="field-body">
              <div className="field is-narrow">
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      name="boot_size"
                      value={item.boot_size || ""}
                      onChange={onChange}
                    >
                      <option
                        value={
                          inventoryMeta.lists.sizes.find(
                            (l) => l.label === "NA",
                          ).id
                        }
                      >
                        --
                      </option>
                      {inventoryMeta.lists.bootSizes.valueSeq().map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {inventoryMeta.requiresLength(item.category) && (
          <div className="field">
            <label className="label">
              Length (cm) <span style={{ color: "red" }}>*</span>
            </label>
            <div className="control">
              <input
                className="input"
                type="number"
                name="length"
                onChange={onChange}
                value={item.length}
              />
              {errors.len && (
                <div className="has-text-danger has-text-weight-semibold">
                  {errors.len}
                </div>
              )}
            </div>
          </div>
        )}

        {inventoryMeta.isSkates(item.category) && (
          <div className="field">
            <label className="label">Size</label>
            <div className="control">
              <input
                className="input"
                type="number"
                step="0.5"
                min="0"
                name="skate_size"
                onChange={onChange}
                value={item.skate_size ?? ""}
              />
            </div>
          </div>
        )}

        <div className="field-body">
          <input
            type="checkbox"
            name="donate"
            checked={item.donate}
            onClick={() => setItem(item.set("donate", !item.donate))}
          />
          &nbsp;Donate 100% of proceeds from this item to the WES PTA. The PTA
          will take the full sale amount. Consignors will not be paid for this
          item.
        </div>

        {sale?.discount?.active && (
          <div style={{ marginTop: 10 }}>
            <div className="field-body">
              <input
                type="checkbox"
                name="discounted"
                checked={item.discounted}
                onClick={() =>
                  setItem(item.set("discounted", !item.discounted))
                }
              />
              &nbsp;Apply discount to this item
            </div>
            <div style={{ marginLeft: 20, marginTop: 5 }}>
              <small className="has-text-grey">
                Items marked for discount will automatically be reduced by{" "}
                {sale.discount.amount}% starting at{" "}
                {(() => {
                  const [hours, minutes] = sale.discount.time.split(":");
                  const hour = parseInt(hours);
                  const ampm = hour >= 12 ? "PM" : "AM";
                  const displayHour =
                    hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                  return `${displayHour}:${minutes} ${ampm}`;
                })()}
                . The discount will be applied at checkout during the sale.
              </small>
            </div>
          </div>
        )}

        <div
          className="field-body mt-4 pt-3"
          style={{ borderTop: "1px solid #CCCCCC" }}
        >
          <div>
            <input
              type="checkbox"
              name="acknowledged"
              checked={acknowledged}
              onChange={() => setAcknowledged(!acknowledged)}
            />
            <span style={{ color: "red" }}> *</span>
            &nbsp;I understand that the PTA takes 20% of proceeds from all sold
            items. I understand that all unsold items must be picked up on
            Saturday between 3:45 and 4:15pm at WES. Any unsold items not
            picked up by 4:15 on Saturday 11/14 will NOT be retrievable.
            {errors.acknowledged && (
              <div className="has-text-danger has-text-weight-semibold">
                {errors.acknowledged}
              </div>
            )}
          </div>
        </div>

        {/* Save Buttons */}
        <div className="mb-6">
          <button
            style={{ marginTop: 5 }}
            className="button is-success"
            onClick={saveAndAddAnother}
            disabled={mutateInventory.isLoading}
          >
            Save and Add Another
          </button>
          <div style={{ width: 10, display: "inline-block" }}></div>{" "}
          {/* Spacer */}
          <button
            style={{ marginTop: 5 }}
            className="button is-success"
            onClick={save}
            disabled={mutateInventory.isLoading}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
